import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchMyPropertyListing,
  fetchMyAmenityCatalogue,
  fetchMyPropertyAuditLatest,
  updateMyOverview,
  updateMyGuidelines,
  setMyAmenities,
  createMyUnit,
  updateMyUnit,
  deleteMyUnit,
  createMyMedia,
  setMyMediaCategory,
  reorderMyMedia,
  setMyMediaCover,
  deleteMyMedia,
} from '@/services/api/my-property.api';
import type {
  UpdateCoreBody,
  UpsertProfileBody,
  AmenitySelectionInput,
  CreateUnitBody,
  UpdateUnitBody,
  CreateMediaBody,
} from '@/services/api/property-content.api';

/**
 * Tenant-scoped listing-editor hooks (the hotel Property Listing Editor).
 * Mirrors the `usePlatform` property-content hooks structure but in its OWN key
 * space (`['my-property', …]`) with a narrow invalidator so hotel edits feel
 * live-immediately: every mutation refetches just this property's aggregate +
 * its "last edited" line, never a broader tree.
 */

const MY_PROPERTY_KEY = ['my-property'] as const;

export const myPropertyKeys = {
  listing: (id: string) => [...MY_PROPERTY_KEY, id, 'listing'] as const,
  catalogue: [...MY_PROPERTY_KEY, 'amenity-catalogue'] as const,
  auditLatest: (id: string) => [...MY_PROPERTY_KEY, id, 'audit-latest'] as const,
};

/** Narrow invalidation: this property's aggregate + its last-edited header. */
function useInvalidateMyListing() {
  const queryClient = useQueryClient();
  return (id: string) => {
    void queryClient.invalidateQueries({ queryKey: myPropertyKeys.listing(id) });
    void queryClient.invalidateQueries({ queryKey: myPropertyKeys.auditLatest(id) });
  };
}

// ─── Reads ────────────────────────────────────────────────────────────────────

export function useMyPropertyListing(id: string | null) {
  return useQuery({
    queryKey: myPropertyKeys.listing(id ?? ''),
    queryFn: () => fetchMyPropertyListing(id as string),
    enabled: !!id,
  });
}

export function useMyAmenityCatalogue() {
  return useQuery({
    queryKey: myPropertyKeys.catalogue,
    queryFn: fetchMyAmenityCatalogue,
    staleTime: 5 * 60_000,
  });
}

export function useMyPropertyAuditLatest(id: string | null) {
  return useQuery({
    queryKey: myPropertyKeys.auditLatest(id ?? ''),
    queryFn: () => fetchMyPropertyAuditLatest(id as string),
    enabled: !!id,
  });
}

// ─── Mutations ────────────────────────────────────────────────────────────────

export function useUpdateMyOverview(id: string) {
  const invalidate = useInvalidateMyListing();
  return useMutation({
    mutationFn: (body: UpdateCoreBody) => updateMyOverview(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useUpsertMyGuidelines(id: string) {
  const invalidate = useInvalidateMyListing();
  return useMutation({
    mutationFn: (body: UpsertProfileBody) => updateMyGuidelines(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useSetMyAmenities(id: string) {
  const invalidate = useInvalidateMyListing();
  return useMutation({
    mutationFn: (selections: AmenitySelectionInput[]) => setMyAmenities(id, selections),
    onSuccess: () => invalidate(id),
  });
}

export function useCreateMyUnit(id: string) {
  const invalidate = useInvalidateMyListing();
  return useMutation({
    mutationFn: (body: CreateUnitBody) => createMyUnit(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useUpdateMyUnit(id: string) {
  const invalidate = useInvalidateMyListing();
  return useMutation({
    mutationFn: ({ unitId, body }: { unitId: string; body: UpdateUnitBody }) =>
      updateMyUnit(id, unitId, body),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteMyUnit(id: string) {
  const invalidate = useInvalidateMyListing();
  return useMutation({
    mutationFn: (unitId: string) => deleteMyUnit(id, unitId),
    onSuccess: () => invalidate(id),
  });
}

export function useCreateMyMedia(id: string) {
  const invalidate = useInvalidateMyListing();
  return useMutation({
    mutationFn: (body: CreateMediaBody) => createMyMedia(id, body),
    onSuccess: () => invalidate(id),
  });
}

export function useSetMyMediaCategory(id: string) {
  const invalidate = useInvalidateMyListing();
  return useMutation({
    mutationFn: ({ mediaId, category }: { mediaId: string; category: string | null }) =>
      setMyMediaCategory(id, mediaId, category),
    onSuccess: () => invalidate(id),
  });
}

export function useReorderMyMedia(id: string) {
  const invalidate = useInvalidateMyListing();
  return useMutation({
    mutationFn: (orderedIds: string[]) => reorderMyMedia(id, orderedIds),
    onSuccess: () => invalidate(id),
  });
}

export function useSetMyMediaCover(id: string) {
  const invalidate = useInvalidateMyListing();
  return useMutation({
    mutationFn: (mediaId: string) => setMyMediaCover(id, mediaId),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteMyMedia(id: string) {
  const invalidate = useInvalidateMyListing();
  return useMutation({
    mutationFn: (mediaId: string) => deleteMyMedia(id, mediaId),
    onSuccess: () => invalidate(id),
  });
}
